import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const useCounter = (end, duration = 2000, isVisible) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const easeOut  = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(end);
    };
    requestAnimationFrame(step);
  }, [end, duration, isVisible]);
  return count;
};

const colorMap = {
  primary:   { bg: 'bg-white', border: 'border-primary-100',   iconBg: 'bg-primary-100',   icon: 'text-primary-500',   num: 'text-primary-700'   },
  secondary: { bg: 'bg-white', border: 'border-secondary-100', iconBg: 'bg-secondary-100', icon: 'text-secondary-600', num: 'text-primary-700'   },
  accent:    { bg: 'bg-white', border: 'border-accent-300',    iconBg: 'bg-accent-300',    icon: 'text-accent-600',    num: 'text-primary-700'   },
  green:     { bg: 'bg-white', border: 'border-primary-100',   iconBg: 'bg-primary-100',   icon: 'text-primary-500',   num: 'text-primary-700'   },
  white:     { bg: 'bg-white/15', border: 'border-white/20',   iconBg: 'bg-white/25',      icon: 'text-accent-400',    num: 'text-white'         },
};

export default function StatCard({ icon: Icon, number, suffix = '', label, color = 'primary', delay = 0 }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref   = useRef(null);
  const count = useCounter(number, 2000, isVisible);
  const c     = colorMap[color] || colorMap.primary;

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => { if (ref.current) obs.unobserve(ref.current); };
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={`${c.bg} border ${c.border} rounded-2xl p-4 sm:p-6 text-center card-hover backdrop-blur-sm`}
    >
      {Icon && (
        <div className={`inline-flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 rounded-2xl ${c.iconBg} shadow-sm mb-3 sm:mb-4`}>
          <Icon className={`w-5 h-5 sm:w-7 sm:h-7 ${c.icon}`} />
        </div>
      )}
      <div className={`text-3xl sm:text-4xl font-bold font-serif mb-1 ${c.num}`}>
        {count.toLocaleString()}{suffix}
      </div>
      <p className={`font-medium text-xs sm:text-sm ${color === 'white' ? 'text-white/80' : 'text-gray-500'}`}>
        {label}
      </p>
    </motion.div>
  );
}
